import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import type { SettingsEmployee } from './employee-utils';
import { SEARCH_NOVA_SHAREABLE_USERS } from '../../../../queries/chats';

export const PEOPLE_RESULT_LIMIT = 8;

export function useSettingsEmployeeSearch(query: string, enabled = true) {
  const [searchQuery, setSearchQuery] = useState('');
  useDebounce(() => setSearchQuery(query.trim()), 200, [query]);
  const { data, loading, error, refetch } = useQuery(SEARCH_NOVA_SHAREABLE_USERS, {
    variables: { query: searchQuery, limit: PEOPLE_RESULT_LIMIT },
    skip: !enabled || !searchQuery,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const isDebouncing = query.trim() !== searchQuery;
  const employees = useMemo<SettingsEmployee[]>(
    () =>
      !enabled || !searchQuery || isDebouncing
        ? []
        : (data?.employees ?? []).map((employee) => ({
            userUuid: employee.userUuid,
            email: employee.email,
            name: employee.fullName,
            imageUrl: employee.imageUrl ?? undefined,
            firstName: employee.firstName ?? undefined,
            lastName: employee.lastName ?? undefined,
            title: employee.title ?? undefined,
            status: 'active',
          })),
    [data?.employees, enabled, isDebouncing, searchQuery],
  );
  return {
    employees,
    loading: enabled && (loading || isDebouncing),
    error: enabled && !!searchQuery && !isDebouncing ? error : undefined,
    retry: () => void refetch().catch(() => undefined),
  };
}
